import React, { useState, useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Wrench,
  FileText,
  Calendar,
  Bell,
  RefreshCw,
  Eye,
  Image,
  History
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/authService';
import { useNavigate } from 'react-router-dom';

const PredictiveMaintenancePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [resolvedAlerts, setResolvedAlerts] = useState([]);
  const [alertPhotos, setAlertPhotos] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadVehicles();
  }, []);

  useEffect(() => {
    if (selectedTab === 'history' && resolvedAlerts.length === 0) {
      loadHistory();
    }
  }, [selectedTab]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/predictive-maintenance/dashboard');
      setDashboard(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await api.get('/api/predictive-maintenance/vehicles/status');
      setVehicles(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/api/predictive-maintenance/alerts');
      const resolved = (response.data || []).filter(a => a.is_resolved);
      setResolvedAlerts(resolved);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadAlertPhotos = async (alertId) => {
    if (alertPhotos[alertId] !== undefined) return; // já carregado
    try {
      const response = await api.get(`/api/predictive-maintenance/alerts/${alertId}/completion-photos`);
      const photos = response.data?.data || response.data || [];
      setAlertPhotos(prev => ({ ...prev, [alertId]: Array.isArray(photos) ? photos : [] }));
    } catch (error) {
      setAlertPhotos(prev => ({ ...prev, [alertId]: [] }));
    }
  };

  const toggleAlert = (alertId) => {
    if (expandedAlert === alertId) {
      setExpandedAlert(null);
    } else {
      setExpandedAlert(alertId);
      loadAlertPhotos(alertId);
    }
  };

  const getPhotoUrl = (fileUrl) => {
    const baseUrl = (api.defaults?.baseURL || '').replace(/\/$/, '');
    return `${baseUrl}${fileUrl}`;
  };

  const getPhotoLabel = (type) => {
    const labels = { location: 'Local', odometer: 'Hodômetro', service_proof: 'Comprovante' };
    return labels[type] || type;
  };

  const getHealthStatusColor = (status) => {
    const colors = {
      excellent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      good: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      fair: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      poor: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getAlertSeverityColor = (severity) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-500',
      critical: 'bg-orange-100 text-orange-800 border-orange-500',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-500',
      info: 'bg-blue-100 text-blue-800 border-blue-500'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDaysUntil = (days) => {
    if (days < 0) return `${Math.abs(days)} dias atrás`;
    if (days === 0) return 'Hoje';
    if (days === 1) return 'Amanhã';
    return `Em ${days} dias`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const healthDistribution = dashboard?.health_distribution || [];
  const upcomingMaintenances = dashboard?.upcoming_maintenances || [];
  const criticalAlerts = dashboard?.critical_alerts || [];
  const healthTrends = dashboard?.health_trends || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Manutenção Preditiva
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitoramento inteligente da frota
          </p>
        </div>
        <button
          onClick={loadDashboard}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total de Veículos */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Activity className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Frota Ativa</span>
          </div>
          <p className="text-3xl font-bold">{stats.total_vehicles || 0}</p>
          <p className="text-sm opacity-80 mt-2">Veículos monitorados</p>
        </div>

        {/* Alertas Ativos */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Bell className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Alertas</span>
          </div>
          <p className="text-3xl font-bold">{stats.total_active_alerts || 0}</p>
          <p className="text-sm opacity-80 mt-2">
            {stats.critical_alerts || 0} críticos
          </p>
        </div>

        {/* Veículos Críticos */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <AlertTriangle className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Atenção Necessária</span>
          </div>
          <p className="text-3xl font-bold">{stats.vehicles_critical || 0}</p>
          <p className="text-sm opacity-80 mt-2">
            Saúde crítica/ruim
          </p>
        </div>

        {/* Manutenções Agendadas */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Calendar className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Próximos 30 dias</span>
          </div>
          <p className="text-3xl font-bold">{stats.vehicles_scheduled_maintenance || 0}</p>
          <p className="text-sm opacity-80 mt-2">Manutenções agendadas</p>
        </div>
      </div>

      {/* Tabs de Navegação */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setSelectedTab('overview')}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                selectedTab === 'overview'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Visão Geral
            </button>
            <button
              onClick={() => setSelectedTab('alerts')}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                selectedTab === 'alerts'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Alertas Críticos
            </button>
            <button
              onClick={() => setSelectedTab('maintenance')}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                selectedTab === 'maintenance'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Próximas Manutenções
            </button>
            <button
              onClick={() => setSelectedTab('vehicles')}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                selectedTab === 'vehicles'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Status dos Veículos
            </button>
            <button
              onClick={() => setSelectedTab('history')}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                selectedTab === 'history'
                  ? 'border-green-500 text-green-600 dark:text-green-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Histórico
            </button>
          </div>
        </div>

        {/* Conteúdo das Tabs */}
        <div className="p-6">
          {/* Overview */}
          {selectedTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Distribuição de Saúde */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Distribuição de Saúde da Frota
                </h3>
                <div className="space-y-3">
                  {healthDistribution.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getHealthStatusColor(item.status)}`}>
                        {item.status === 'excellent' && 'Excelente'}
                        {item.status === 'good' && 'Bom'}
                        {item.status === 'fair' && 'Regular'}
                        {item.status === 'poor' && 'Ruim'}
                        {item.status === 'critical' && 'Crítico'}
                        {item.status === 'unknown' && 'Desconhecido'}
                      </div>
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-full rounded-full ${
                            item.status === 'excellent' ? 'bg-green-500' :
                            item.status === 'good' ? 'bg-blue-500' :
                            item.status === 'fair' ? 'bg-yellow-500' :
                            item.status === 'poor' ? 'bg-orange-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${(item.count / stats.total_vehicles) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white w-12 text-right">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tendências */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Tendência de Saúde (30 dias)
                </h3>
                {healthTrends.length > 0 ? (
                  <div className="h-40 flex items-end justify-between gap-1">
                    {healthTrends.slice(-15).map((trend, index) => {
                      const height = (trend.avg_score / 100) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center">
                          <div
                            className={`w-full rounded-t ${
                              trend.avg_score >= 70 ? 'bg-green-500' :
                              trend.avg_score >= 50 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ height: `${height}%` }}
                            title={`${formatDate(trend.date)}: ${trend.avg_score}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Sem dados de tendência disponíveis
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Alertas Críticos */}
          {selectedTab === 'alerts' && (
            <div className="space-y-4">
              {criticalAlerts.length > 0 ? (
                criticalAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`border-l-4 p-4 rounded-lg ${getAlertSeverityColor(alert.alert_severity)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-5 h-5" />
                          <h4 className="font-semibold">{alert.title}</h4>
                        </div>
                        <p className="text-sm mb-2">{alert.message}</p>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="font-medium">
                            {alert.model} - {alert.license_plate}
                          </span>
                          {alert.predicted_failure_date && (
                            <span>
                              Previsão: {formatDaysUntil(alert.days_until_failure)}
                            </span>
                          )}
                          {alert.confidence_level && (
                            <span>
                              Confiança: {alert.confidence_level}%
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/vehicles/${alert.vehicle_id}`)}
                        className="ml-4 p-2 hover:bg-white/50 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhum alerta crítico no momento
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Próximas Manutenções */}
          {selectedTab === 'maintenance' && (
            <div className="space-y-4">
              {upcomingMaintenances.length > 0 ? (
                upcomingMaintenances.map((maintenance) => (
                  <div
                    key={maintenance.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <Wrench className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {maintenance.model} - {maintenance.license_plate}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {maintenance.description}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          Técnico: {maintenance.technician_name || 'Não atribuído'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium mb-1">
                        {formatDaysUntil(maintenance.days_until_maintenance)}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(maintenance.scheduled_date)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhuma manutenção agendada nos próximos 30 dias
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Histórico de Alertas Resolvidos */}
          {selectedTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Alertas finalizados pelos motoristas com fotos de comprovação
                </p>
                <button
                  onClick={loadHistory}
                  className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600"
                >
                  <RefreshCw className="w-4 h-4" />
                  Atualizar
                </button>
              </div>

              {historyLoading ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : resolvedAlerts.length === 0 ? (
                <div className="text-center py-16">
                  <History className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Nenhum alerta resolvido ainda
                  </p>
                </div>
              ) : (
                resolvedAlerts.map((alert) => {
                  const isExpanded = expandedAlert === alert.id;
                  const photos = alertPhotos[alert.id] || [];
                  const severityBg = {
                    urgent: 'border-red-300 dark:border-red-700',
                    critical: 'border-orange-300 dark:border-orange-700',
                    warning: 'border-yellow-300 dark:border-yellow-700',
                    info: 'border-blue-300 dark:border-blue-700',
                  }[alert.severity] || 'border-gray-300 dark:border-gray-600';

                  return (
                    <div
                      key={alert.id}
                      className={`border rounded-xl overflow-hidden ${severityBg}`}
                    >
                      {/* Cabeçalho do alerta */}
                      <button
                        onClick={() => toggleAlert(alert.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {alert.vehicle_plate}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {alert.vehicle_model}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                getAlertSeverityColor(alert.severity).replace('border-', '').split(' ').slice(0, 2).join(' ')
                              }`}>
                                {alert.severity}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5 truncate">
                              {alert.title}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                            Resolvido
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatDate(alert.resolved_at)}
                          </p>
                          {alertPhotos[alert.id] !== undefined && alertPhotos[alert.id].length > 0 && (
                            <p className="text-xs text-blue-500 mt-0.5 flex items-center justify-end gap-1">
                              <Image className="w-3 h-3" />
                              {alertPhotos[alert.id].length} foto{alertPhotos[alert.id].length !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      </button>

                      {/* Detalhes expandidos */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            {alert.message}
                          </p>
                          <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4">
                            <span>Criado: {formatDate(alert.created_at)}</span>
                            {alert.confidence && <span>Confiança: {alert.confidence}%</span>}
                          </div>

                          {/* Fotos de comprovação */}
                          {photos.length > 0 ? (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                <Image className="w-4 h-4" />
                                Fotos de comprovação
                              </h5>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {photos.map((photo) => (
                                  <div key={photo.id} className="relative">
                                    <a
                                      href={getPhotoUrl(photo.file_url)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img
                                        src={getPhotoUrl(photo.file_url)}
                                        alt={getPhotoLabel(photo.photo_type)}
                                        className="w-full aspect-square object-cover rounded-lg border border-gray-200 dark:border-gray-600 hover:opacity-90 transition-opacity"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    </a>
                                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                                      {getPhotoLabel(photo.photo_type)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : alertPhotos[alert.id] !== undefined ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                              Nenhuma foto registrada para este alerta
                            </p>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Carregando fotos...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Status dos Veículos */}
          {selectedTab === 'vehicles' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Veículo
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Health Score
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Alertas
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Última Manutenção
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Próxima Manutenção
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle) => (
                    <tr
                      key={vehicle.vehicle_id}
                      className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {vehicle.model}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {vehicle.license_plate}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            {vehicle.health_score || '-'}
                          </span>
                          {vehicle.health_status && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getHealthStatusColor(vehicle.health_status)}`}>
                              {vehicle.health_status === 'excellent' && 'Excelente'}
                              {vehicle.health_status === 'good' && 'Bom'}
                              {vehicle.health_status === 'fair' && 'Regular'}
                              {vehicle.health_status === 'poor' && 'Ruim'}
                              {vehicle.health_status === 'critical' && 'Crítico'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {vehicle.active_alerts > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs font-medium">
                            <AlertTriangle className="w-3 h-3" />
                            {vehicle.active_alerts}
                          </span>
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(vehicle.last_maintenance_date)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(vehicle.next_maintenance_date)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => navigate(`/vehicles/${vehicle.vehicle_id}`)}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        >
                          <Eye className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vehicles.length === 0 && (
                <div className="text-center py-12">
                  <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Nenhum veículo encontrado
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictiveMaintenancePage;